import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Logo from '../../Logo';

export const Login = ({ apiUrl }: { apiUrl: string }): JSX.Element => {
  const { agentId } = useParams();
  const [searchParams] = useSearchParams();
  const status = searchParams.get('status');
  const missingScopes = searchParams.get('missing_scopes');
  const error = searchParams.get('error');
  const stateParam = searchParams.get('state');
  const [googleLink, setGoogleLink] = useState<string>('');
  const [azureLink, setAzureLink] = useState<string>('');
  const [agentCalendarInfo, setAgentCalendarInfo] = useState<{
    hasGoogle: boolean;
    hasAzure: boolean;
  } | null>(null);
  const [agentNotFoundError, setAgentNotFoundError] = useState<string | null>(
    null,
  );

  useEffect(() => {
    const fetchGoogleLink = async () => {
      try {
        let url = `${apiUrl}/api/auth/google/${agentId}`;
        if (stateParam) {
          url += `?state=${encodeURIComponent(stateParam)}`;
        }
        const response = await fetch(url);
        const jsonData = await response.json();
        setGoogleLink(jsonData.url);
      } catch (err) {
        console.error('An error occurred while fetching data:', err);
      }
    };
    const fetchAzureLink = async () => {
      try {
        let url = `${apiUrl}/api/auth/azure/${agentId}`;
        if (stateParam) {
          url += `?state=${encodeURIComponent(stateParam)}`;
        }
        const response = await fetch(url);
        const jsonData = await response.json();
        setAzureLink(jsonData.url);
      } catch (err) {
        console.error('An error occurred while fetching data:', err);
      }
    };
    const fetchAgentInfo = async () => {
      try {
        const response = await fetch(
          `${apiUrl}/api/agent/marketing/${agentId}`,
        );
        if (response.ok) {
          const jsonData = await response.json();
          setAgentNotFoundError(null);
          setAgentCalendarInfo({
            hasGoogle: Boolean(jsonData.has_google),
            hasAzure: Boolean(jsonData.has_azure),
          });
        } else {
          setAgentCalendarInfo({ hasGoogle: false, hasAzure: false });
          if (response.status === 404) {
            setAgentNotFoundError('Agent not found.');
          }
        }
      } catch {
        setAgentCalendarInfo({ hasGoogle: false, hasAzure: false });
      }
    };
    fetchGoogleLink().then().catch(console.log);
    fetchAzureLink().then().catch(console.log);
    fetchAgentInfo().then().catch(console.log);
  }, [agentId, apiUrl, stateParam]);

  const formatScopeName = (scope: string): string => {
    // Remove common prefixes and format for display
    const formatted = scope
      .replace(/^https:\/\/www\.googleapis\.com\/auth\//, '')
      .replace(/^https:\/\/graph\.microsoft\.com\//, '')
      .replace(/\./g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());

    // Handle specific scope names
    const scopeMap: Record<string, string> = {
      'userinfo.profile': 'Profile Information',
      'userinfo.email': 'Email Address',
      'calendar.events': 'Calendar Events',
      'calendar.readonly': 'Calendar Read Access',
      'gmail.send': 'Send Emails',
      'gmail.compose': 'Compose Emails',
      'gmail.modify': 'Modify Emails',
      'gmail.readonly': 'Read Emails',
      'User.ReadBasic.All': 'Basic Profile Information',
      'Calendars.Read': 'Read Calendar',
      'Calendars.ReadWrite': 'Read and Write Calendar',
      'Mail.Read': 'Read Emails',
      'Mail.ReadWrite': 'Read and Write Emails',
      'Mail.Send': 'Send Emails',
      'Mail.ReadBasic': 'Basic Email Access',
      'Mail.ReadWrite.All': 'Full Email Access',
      'Mail.Send.Shared': 'Send Shared Emails',
      'Mail.Read.Shared': 'Read Shared Emails',
      'Mail.ReadWrite.Shared': 'Read and Write Shared Emails',
      'IMAP.AccessAsUser.All': 'IMAP Email Access',
      'POP.AccessAsUser.All': 'POP Email Access',
      'SMTP.Send': 'SMTP Send Access',
      offline_access: 'Offline Access',
      openid: 'OpenID Connect',
      profile: 'Profile Information',
      email: 'Email Address',
    };

    return scopeMap[formatted.toLowerCase()] || formatted;
  };

  if (status === 'scope_error') {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          paddingTop: '20px',
          maxWidth: '600px',
          margin: '0 auto',
          textAlign: 'center',
        }}
      >
        <Logo />
        <div style={{ marginTop: '20px' }}>
          <h1 style={{ color: '#d32f2f' }}>Additional Permissions Required</h1>
          <p
            style={{
              fontSize: '16px',
              lineHeight: '1.5',
              marginBottom: '20px',
            }}
          >
            To provide you with the full functionality, we need access to all
            requested permissions. Please try again and make sure to grant all
            the permissions when prompted.
          </p>
          {missingScopes && (
            <div style={{ marginBottom: '20px' }}>
              <p style={{ fontWeight: 'bold', marginBottom: '10px' }}>
                Missing permissions:
              </p>
              <ul style={{ textAlign: 'left', display: 'inline-block' }}>
                {missingScopes.split(',').map((scope, index) => (
                  <li key={index} style={{ marginBottom: '5px' }}>
                    {formatScopeName(scope.trim())}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {error && (
            <div style={{ marginBottom: '20px' }}>
              <p style={{ fontWeight: 'bold', marginBottom: '10px' }}>Error:</p>
              <p style={{ color: '#d32f2f' }}>{error}</p>
            </div>
          )}
          <div style={{ marginTop: '20px' }}>
            <p style={{ marginBottom: '15px', fontWeight: 'bold' }}>
              Please try signing in again with your preferred provider:
            </p>
            {agentCalendarInfo === null ? (
              <p style={{ color: '#555', fontSize: '14px' }}>Loading…</p>
            ) : (
              <>
                {!agentCalendarInfo.hasAzure && (
                  <div style={{ marginBottom: '15px' }}>
                    <img
                      alt="Google Sign In"
                      onClick={() => (window.location.href = googleLink)}
                      src="/google-sign-in.svg"
                      style={{
                        cursor: 'pointer',
                        width: '100%',
                        maxWidth: '250px',
                      }}
                    />
                  </div>
                )}
                {!agentCalendarInfo.hasGoogle && (
                  <div>
                    <img
                      alt="Azure Sign In"
                      onClick={() => (window.location.href = azureLink)}
                      src="/ms-sign-in.svg"
                      style={{
                        cursor: 'pointer',
                        width: '100%',
                        maxWidth: '250px',
                      }}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          paddingTop: '20px',
        }}
      >
        <Logo />
        <div style={{ marginTop: '30px', textAlign: 'center' }}>
          <h2>You&apos;re All Set!</h2>
          <p>
            We&apos;re loading your data now. Feel free to close this window —
            we&apos;ll let you know when it&apos;s ready.
          </p>
        </div>
      </div>
    );
  }

  if (agentNotFoundError) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          height: '100vh',
          width: '100vw',
          paddingTop: '100px',
        }}
      >
        <Logo />
        <p
          style={{
            marginTop: '24px',
            textAlign: 'center',
            color: '#d32f2f',
            fontSize: '16px',
            padding: '0 40px',
          }}
        >
          {agentNotFoundError}
        </p>
      </div>
    );
  }

  return (
    <div>
      <Helmet>
        <title>HouseWhisper - Sign In</title>
        <meta
          content="Enable calendar access by signing into HouseWhisper with your calendar provider"
          name="description"
        />
        <meta content="HouseWhisper - Sign In" property="og:title" />
        <meta
          content="Enable calendar access by signing into HouseWhisper with your calendar provider"
          property="og:description"
        />
        <meta
          content={`${window.location.origin}/logo.png`}
          property="og:image"
        />
        <meta content="website" property="og:type" />
        <meta content={window.location.href} property="og:url" />
        <meta content="HouseWhisper" property="og:site_name" />
        <meta content="summary_large_image" name="twitter:card" />
        <meta content="HouseWhisper - Sign In" name="twitter:title" />
        <meta
          content="Enable calendar access by signing into HouseWhisper with your calendar provider"
          name="twitter:description"
        />
        <meta
          content={`${window.location.origin}/logo.png`}
          name="twitter:image"
        />
      </Helmet>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          height: '100vh',
          width: '100vw',
          paddingTop: '100px',
        }}
      >
        <div
          style={{ width: '100%', textAlign: 'center', marginBottom: '20px' }}
        >
          <Logo />
        </div>
        <div
          style={{
            width: '100%',
          }}
        >
          <div
            style={{
              fontSize: '24px',
              lineHeight: '32px',
              textAlign: 'center',
              fontWeight: '300',
              padding: '0 40px',
            }}
          >
            Enable calendar access by signing into HouseWhisper with your
            calendar provider
          </div>
          {agentCalendarInfo === null ? (
            <p
              style={{
                marginTop: '50px',
                textAlign: 'center',
                color: '#555',
                fontSize: '14px',
              }}
            >
              Loading…
            </p>
          ) : (
            <>
              {!agentCalendarInfo.hasAzure && (
                <div
                  style={{
                    marginTop: '50px',
                    width: '100%',
                  }}
                >
                  <img
                    alt="Google Sign In"
                    onClick={() => (window.location.href = googleLink)}
                    src="/google-sign-in.svg"
                    style={{
                      cursor: 'pointer',
                      width: '100%',
                      maxWidth: '250px',
                    }}
                  />
                </div>
              )}
              {!agentCalendarInfo.hasGoogle && (
                <div
                  style={{
                    marginTop: agentCalendarInfo.hasGoogle ? '50px' : '20px',
                    width: '100%',
                  }}
                >
                  <img
                    alt="Azure Sign In"
                    onClick={() => (window.location.href = azureLink)}
                    src="/ms-sign-in.svg"
                    style={{
                      cursor: 'pointer',
                      width: '100%',
                      maxWidth: '250px',
                    }}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
